import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronsUpDown, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

export const AMENITY_OPTIONS = [
  "Pool", "Jacuzzi", "WiFi", "TV", "Hot water", "Sea view", "River view", "City view",
  "Green view", "Mountain view", "Hill view", "No view", "Balcony", "Terrace",
  "Parking", "Four wheeler parking", "Two wheeler parking", "Shuttle services",
  "Fridge", "Gas stove", "Utensils", "Cookware", "Microwave", "Water purifier",
  "Washing machine", "Dining table", "Kitchenette", "Children crib", "Wardrobe", "Cloth hanger",
  "Bath towel", "Linen", "Bed sheet", "Blanket", "Extra sheet", "Pool towel",
  "Power backup", "Room lock", "Music system", "Netflix", "Bar",
  "Self check-in", "Paperless check-in", "Accept cards", "Accept UPI", "Accept cash",
  "Caretaker", "Service on call", "Rent a car", "Rent a bike", "Taxi services", "Airport pick-up",
  "Beach access", "Private beach", "Beach touch",
  "Temple", "Chapel", "Mosque", "Veg only", "Veg & non-veg both", "Jain only",
  "Security camera", "Cameras", "Fire extinguisher", "Fire alarm", "24/7 security",
  "Private property", "Private access", "Shared",
  "Host on site", "Host off site", "Private host", "Professional host",
];

export function AmenitiesSelect({ value = [], onChange, testId = "amenities-select" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = value || [];

  const toggle = (item) => {
    if (selected.includes(item)) onChange(selected.filter((s) => s !== item));
    else onChange([...selected, item]);
  };
  const remove = (item) => onChange(selected.filter((s) => s !== item));

  const trimmed = query.trim();
  const canAddCustom = trimmed && !AMENITY_OPTIONS.some((o) => o.toLowerCase() === trimmed.toLowerCase()) && !selected.some((s) => s.toLowerCase() === trimmed.toLowerCase());

  return (
    <div data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-trigger`}
            className="w-full flex items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm text-left hover:border-[#FF385C] transition-colors"
          >
            <span className={selected.length ? "text-[#222]" : "text-muted-foreground"}>
              {selected.length ? `${selected.length} amenit${selected.length > 1 ? "ies" : "y"} selected` : "Select amenities"}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl" align="start">
          <Command>
            <CommandInput placeholder="Search or add amenity..." value={query} onValueChange={setQuery} data-testid={`${testId}-search`} />
            <CommandList className="max-h-64">
              <CommandEmpty>
                {canAddCustom ? (
                  <button
                    type="button"
                    data-testid={`${testId}-add-custom`}
                    onClick={() => { toggle(trimmed); setQuery(""); }}
                    className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-[#FF385C] hover:bg-[#FFF2F4] rounded"
                  >
                    <Plus className="h-4 w-4" /> Add "{trimmed}"
                  </button>
                ) : "No amenity found."}
              </CommandEmpty>
              <CommandGroup>
                {canAddCustom && (
                  <CommandItem value={`add-${trimmed}`} onSelect={() => { toggle(trimmed); setQuery(""); }} data-testid={`${testId}-add-inline`}>
                    <Plus className="mr-2 h-4 w-4 text-[#FF385C]" /> Add "{trimmed}"
                  </CommandItem>
                )}
                {AMENITY_OPTIONS.map((item) => {
                  const isSel = selected.includes(item);
                  return (
                    <CommandItem key={item} value={item} onSelect={() => toggle(item)} data-testid={`${testId}-option-${item}`}>
                      <Check className={cn("mr-2 h-4 w-4", isSel ? "opacity-100 text-[#FF385C]" : "opacity-0")} />
                      {item}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testId}-chips`}>
          {selected.map((item) => (
            <Badge key={item} variant="secondary" className="rounded-full pl-2.5 pr-1 py-0.5 gap-1 bg-[#F0F0F4] text-[#222] hover:bg-[#E8E8EC]">
              {item}
              <button type="button" data-testid={`${testId}-remove-${item}`} onClick={() => remove(item)} className="hover:bg-black/10 rounded-full p-0.5">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default AmenitiesSelect;
